/**
 * Password Strength Indicator Component
 * Displays password strength and requirement validation
 */

import { useMemo } from 'react'
import type { PasswordStrength, PasswordRequirement } from '../types/registration'

interface PasswordStrengthIndicatorProps {
  password: string
  className?: string
}

/**
 * Calculate password strength based on requirements met
 */
function calculatePasswordStrength(password: string): PasswordStrength {
  if (!password) return 'weak'

  const requirements = [
    password.length >= 8, // Minimum length
    /[a-z]/.test(password), // Lowercase
    /\d/.test(password), // Number
  ]

  const metCount = requirements.filter(Boolean).length

  if (metCount <= 1) return 'weak'
  if (metCount === 2) return 'medium'
  return 'strong'
}

/**
 * Password Strength Indicator
 * Shows visual strength meter and requirement checklist
 */
export function PasswordStrengthIndicator({ password, className = '' }: PasswordStrengthIndicatorProps) {
  const strength = useMemo(() => calculatePasswordStrength(password), [password])

  const requirements: PasswordRequirement[] = useMemo(
    () => [
      {
        label: 'At least 8 characters',
        met: password.length >= 8,
      },
      {
        label: 'One lowercase letter (a-z)',
        met: /[a-z]/.test(password),
      },
      {
        label: 'One number (0-9)',
        met: /\d/.test(password),
      },
    ],
    [password]
  )

  const strengthColors = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
  }

  const strengthLabels = {
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
  }

  const strengthWidths = {
    weak: 'w-1/3',
    medium: 'w-2/3',
    strong: 'w-full',
  }

  // Don't show indicator if no password entered
  if (!password) return null

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Strength Bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Password Strength</span>
          <span
            className={`text-xs font-semibold ${
              strength === 'weak'
                ? 'text-red-600'
                : strength === 'medium'
                  ? 'text-yellow-600'
                  : 'text-green-600'
            }`}
          >
            {strengthLabels[strength]}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full ${strengthColors[strength]} ${strengthWidths[strength]} transition-all duration-300`}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
        <ul className="space-y-1">
          {requirements.map((req, index) => (
            <li key={index} className="flex items-start gap-2 text-xs">
              <span className="flex-shrink-0 mt-0.5">
                {req.met ? (
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <span className={req.met ? 'text-gray-700' : 'text-gray-500'}>{req.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
